import React from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesApiOverview } from '../wesPalletStagingApi';
import {
  formatApiCount,
  formatApiPercent,
  formatCompactCount,
  stagedForScope,
  toHighlight,
  type WesLocationHighlight,
} from '../wesPalletStagingMappers';
import type { WesStagingScope } from '../wesPalletStagingStaticRows';
import WesSectionState, { type WesSectionStatus } from './WesSectionState';
import { WES_CARD } from './wesCardStyle';
import { WesTooltipTarget } from './WesTooltip';

export const WES_SCOPE_LABEL: Record<WesStagingScope, string> = {
  all: 'All',
  audit: 'Audit-Related',
  other: 'Other',
};

export const WES_STAGED_TITLE: Record<WesStagingScope, string> = {
  all: 'Total Staged Pallets',
  audit: 'Staged Pallets (Audit-Related)',
  other: 'Staged Pallets (Other)',
};

const SCOPE_BADGE: Record<WesStagingScope, string> = {
  all: 'All Staging',
  audit: 'Audit-Related Staging',
  other: 'Other Staging',
};

// Below this width the hero sits on top and the four tiles form two columns (mockup responsive rule).
const STACK_BREAKPOINT = 900;

interface WesKpiCardsProps {
  overview: WesApiOverview | null;
  scope: WesStagingScope;
  status?: WesSectionStatus;
}

const twoDecimals = (pct: number | null | undefined) => (pct === null || pct === undefined ? '-' : `${pct.toFixed(2)}%`);

const WesKpiCards: React.FC<WesKpiCardsProps> = ({ overview, scope, status }) => {
  const { width } = useWindowDimensions();
  const stacked = width < STACK_BREAKPOINT;
  const totals = overview?.totals;
  const staged = totals ? stagedForScope(totals, scope) : null;
  const total = totals?.total ?? 0;
  const scopeLower = WES_SCOPE_LABEL[scope].toLowerCase();
  const allScopeCounts = scope === 'all' ? `\n${formatApiCount(staged)} of ${formatApiCount(total)} pallets staged` : '';
  const auditBreakdown =
    scope === 'audit'
      ? `\n${formatApiCount(totals?.qualityAudit)} quality audit pallets | ${formatApiCount(totals?.shortAudit)} short audit pallets`
      : '';
  const heroTooltip = `% of Pallets Staged = ${WES_STAGED_TITLE[scope]} / Total Pallets${allScopeCounts}${auditBreakdown}`;
  const heroTooltipWidth = Math.min(420, width - 32);

  // Value on top, its supporting text underneath; an optional highlight follows the text in bold blue.
  const tile = (key: string, label: string, value: string, sub: string, highlight?: string, valueTooltip?: string) => (
    <View style={styles.tile} testID={`wes-card-${key}`}>
      <Text style={styles.label} numberOfLines={2}>{label}</Text>
      {valueTooltip ? (
        <WesTooltipTarget text={valueTooltip} testID={`wes-${key}-location-name`} style={styles.locationNameTarget} maxWidth={280}>
          <Text style={styles.value} numberOfLines={1}>{value}</Text>
        </WesTooltipTarget>
      ) : <Text style={styles.value} numberOfLines={1}>{value}</Text>}
      <Text style={styles.sub}>
        {sub}
        {highlight ? <Text style={styles.subBlue}>{` · ${highlight}`}</Text> : null}
      </Text>
    </View>
  );

  const locationTile = (key: string, label: string, group: WesLocationHighlight | null) =>
    group
      ? tile(key, label, group.label, `${formatApiCount(group.staged)} of ${formatApiCount(group.total)} pallets staged`, `${formatApiPercent(group.pct)} staged`, group.label)
      : tile(key, label, '-', 'No locations in scope');

  const hero = (
    <View style={styles.hero} testID="wes-card-percent">
      <WesTooltipTarget
        text={heroTooltip}
        testID="wes-hero-info"
        style={styles.info}
        placement={scope === 'audit' ? 'above' : 'left'}
        bubbleLeft={scope === 'audit' ? (stacked ? 22 - heroTooltipWidth : 11 - heroTooltipWidth / 2) : 0}
        maxWidth={heroTooltipWidth}
      >
        {({ open }) => (
          <View style={[styles.infoIcon, open && styles.infoIconOpen]}>
            <Text style={[styles.infoGlyph, open && styles.infoGlyphOpen]}>i</Text>
          </View>
        )}
      </WesTooltipTarget>
      <View style={styles.badge}>
        <Text style={styles.badgeText}>{SCOPE_BADGE[scope]}</Text>
      </View>
      <Text style={styles.heroLabel}>% of Pallets Staged</Text>
      <View style={styles.heroRow}>
        <Text style={styles.heroValue}>{twoDecimals(overview?.stagedPct)}</Text>
        <Text style={styles.heroSub}>
          {total > 0 ? `${formatApiCount(staged)} of ${formatApiCount(total)} pallets staged · ${scopeLower}` : '0 of 0 pallets staged'}
        </Text>
      </View>
    </View>
  );

  const totalTile = tile('total', 'Total Pallets', formatCompactCount(total), `${formatApiCount(total)} pallets`);
  const stagedTile = tile('staged', WES_STAGED_TITLE[scope], formatCompactCount(staged), `${formatApiCount(staged)} pallets staged · ${scopeLower}`);
  const highestTile = locationTile('highest', 'Highest % of Pallets Staged', toHighlight(overview?.highest, scope));
  const lowestTile = locationTile('lowest', 'Lowest % of Pallets Staged', toHighlight(overview?.lowest, scope));

  return (
    <WesSectionState status={status} skeleton="cards" testID="wes-kpi-cards">
      {stacked ? (
        <View style={styles.stack} testID="wes-kpi-cards">
          {hero}
          <View style={styles.pair}>{totalTile}{stagedTile}</View>
          <View style={styles.pair}>{highestTile}{lowestTile}</View>
        </View>
      ) : (
        <View style={styles.grid} testID="wes-kpi-cards">
          <View style={styles.heroCol}>{hero}</View>
          <View style={styles.col}>{totalTile}{stagedTile}</View>
          <View style={styles.col}>{highestTile}{lowestTile}</View>
        </View>
      )}
    </WesSectionState>
  );
};

const styles = StyleSheet.create({
  grid: { flexDirection: 'row', columnGap: 12, alignItems: 'stretch' },
  heroCol: { flex: 1.15 },
  col: { flex: 1, rowGap: 12 },
  stack: { rowGap: 12 },
  pair: { flexDirection: 'row', columnGap: 12 },
  tile: { ...WES_CARD, flex: 1, paddingVertical: 10, paddingHorizontal: 14, rowGap: 3, minWidth: 0 },
  label: { fontSize: 11, lineHeight: 16, fontFamily: FontFamily.bold, color: '#616161', textTransform: 'uppercase', letterSpacing: 0.22 },
  value: { fontSize: 34, lineHeight: 42, fontFamily: FontFamily.bold, color: '#2D2D2D', flexShrink: 1 },
  locationNameTarget: { flexShrink: 1, minWidth: 0 },
  sub: { fontSize: 12.5, fontFamily: FontFamily.regular, color: '#616161' },
  subBlue: { fontFamily: FontFamily.bold, color: '#005CBC' },
  hero: {
    ...WES_CARD,
    flex: 1,
    backgroundColor: '#F3F7FD',
    borderWidth: 1,
    borderColor: '#C6DCFB',
    paddingVertical: 14,
    paddingHorizontal: 20,
    justifyContent: 'center',
    rowGap: 4,
  },
  info: { position: 'absolute', top: 10, right: 10, zIndex: 5 },
  infoIcon: { width: 22, height: 22, borderRadius: 11, borderWidth: 1.5, borderColor: '#818181', backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center' },
  infoGlyph: { fontSize: 12, fontStyle: 'italic', fontWeight: '700', color: '#616161' },
  infoIconOpen: { borderColor: '#034895' },
  infoGlyphOpen: { color: '#034895' },
  badge: { alignSelf: 'flex-start', backgroundColor: '#034895', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3 },
  badgeText: { fontSize: 11, fontFamily: FontFamily.bold, color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: 0.55 },
  heroLabel: { fontSize: 13, fontFamily: FontFamily.bold, color: '#034895', textTransform: 'uppercase', letterSpacing: 0.52 },
  heroRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', columnGap: 16, rowGap: 4 },
  heroValue: { fontSize: 54, lineHeight: 60, fontFamily: FontFamily.bold, color: '#034895' },
  heroSub: { flexShrink: 1, fontSize: 13, lineHeight: 19, fontFamily: FontFamily.regular, color: '#616161', textAlign: 'right', paddingBottom: 8 },
});

export default WesKpiCards;
